import { createContext, useContext, type ReactNode } from 'react'
import type { BackupCryptoParams, BackupDeviceInfo } from './format'

/**
 * **Der Weg zum Hauptprozess** für Backups (#158). Dateien liest und schreibt nur er; der Renderer
 * schickt Bytes und einen Namensvorschlag, nie einen Pfad. Stories und Tests setzen eine eigene
 * Brücke über {@link BackupBridgeProvider} ein.
 */

export type SaveKind = 'backup' | 'csv' | 'markdown'

export interface OpenedBackup {
  name: string
  bytes: Uint8Array
}

/** Was „vorher aus der Cloud holen“ ergab (Entscheidung 20); `synced: false` heißt: kein Geräte-Sync. */
export type MainSyncResult =
  | { ok: true; synced: boolean }
  | { ok: false; reason: 'offline' | 'failed' | 'timeout' }

export type BackupLocation = 'folder' | 'app'

/** Ein automatisches Backup in der Liste — eigenes oder das eines anderen Geräts im selben Ordner. */
export interface AutoBackupEntry {
  name: string
  /** `app`: im App-Ordner — der Standard oder die Ausweichstelle, wenn der gewählte Ordner fehlte. */
  location: BackupLocation
  /** `JJJJ-MM-TT`. */
  date: string
  deviceName: string
  size: number
  own: boolean
  /** Mit Passwort geschützt — die Liste zeigt ein Schloss. */
  encrypted: boolean
}

export interface AutoBackupStatus {
  enabled: boolean
  folder: string
  isDefaultFolder: boolean
  /** Ob der gewählte Ordner gerade erreichbar ist; sonst landen Backups im App-Ordner. */
  reachable: boolean
  hasToday: boolean
  /** Erst die eigenen, dann die anderer Geräte — je neueste zuerst. */
  backups: AutoBackupEntry[]
}

export type ChooseFolderResult =
  | { changed: false; notWritable?: boolean; status: AutoBackupStatus }
  | { changed: true; moveFailed: boolean; status: AutoBackupStatus }

export interface PasswordStatus {
  enabled: boolean
  /** Ob das System einen sicheren Speicher für das Passwort hat. */
  available: boolean
}

export type DecryptResult = { ok: true; bytes: Uint8Array } | { ok: false; reason: 'wrong-password' | 'invalid' }

export interface BackupBridge {
  deviceInfo(): Promise<BackupDeviceInfo>
  /** Speichern-Dialog; `saved: false`, wenn abgebrochen. */
  saveAs(bytes: Uint8Array, name: string, kind: SaveKind): Promise<{ saved: boolean; name?: string }>
  /** Öffnen-Dialog, nur `.happytask`; `null`, wenn abgebrochen. */
  open(): Promise<OpenedBackup | null>
  /** Die per Doppelklick geöffnete Datei, genau einmal. */
  takeOpenedFile(): Promise<OpenedBackup | null>
  /**
   * Aus dem Hauptfenster („Ich habe ein Backup …“): Öffnen-Dialog, mit Datei dann „Backup
   * wiederherstellen“ in den Einstellungen. `false`, wenn abgebrochen.
   */
  openAndRestore(): Promise<boolean>
  /** Bittet das Hauptfenster um einen Sync und wartet darauf. */
  syncInMain(): Promise<MainSyncResult>
  autoStatus(): Promise<AutoBackupStatus>
  /** Das Backup von heute; schreibt nichts, wenn es abgeschaltet ist oder heute schon eins liegt. */
  autoWrite(bytes: Uint8Array): Promise<{ written: boolean; status: AutoBackupStatus }>
  /** Ein Backup aus der Liste; `null`, wenn es nicht mehr da ist. */
  autoRead(entry: Pick<AutoBackupEntry, 'name' | 'location'>): Promise<OpenedBackup | null>
  autoSetEnabled(enabled: boolean): Promise<AutoBackupStatus>
  /** Ordner-Dialog; zieht die eigenen automatischen Backups mit. */
  chooseFolder(): Promise<ChooseFolderResult>
  /** Zeigt den Ordner im Finder bzw. Explorer. */
  reveal(): Promise<void>
  passwordStatus(): Promise<PasswordStatus>
  /** Legt das Passwort fest oder ändert es; ältere Backups behalten ihres. */
  setPassword(password: string): Promise<PasswordStatus>
  /** Neue Backups ohne Passwort; die bisherigen gehen auf diesem Gerät weiter ohne Nachfrage auf. */
  clearPassword(): Promise<PasswordStatus>
  /** Verschlüsselt mit dem Passwort dieses Geräts — das Passwort selbst bleibt im Hauptprozess. */
  encrypt(plain: Uint8Array): Promise<{ data: Uint8Array; params: BackupCryptoParams }>
  /** Ohne `password` mit den Passwörtern dieses Geräts, sonst nur mit der Eingabe. */
  decrypt(data: Uint8Array, params: BackupCryptoParams, password?: string): Promise<DecryptResult>
}

const invoke = <T,>(channel: string, ...args: unknown[]): Promise<T> => {
  if (!window.api?.invoke) return Promise.reject(new Error('Backups gibt es nur in der Desktop-App'))
  return window.api.invoke(channel, ...args) as Promise<T>
}

export const electronBackupBridge: BackupBridge = {
  deviceInfo: () => invoke('backup:device-info'),
  saveAs: (bytes, name, kind) => invoke('backup:save-as', bytes, name, kind),
  open: () => invoke('backup:open'),
  takeOpenedFile: () => invoke('backup:take-opened-file'),
  openAndRestore: () => invoke('backup:open-and-restore'),
  syncInMain: () => invoke('backup:sync-in-main'),
  autoStatus: () => invoke('backup:auto-status'),
  autoWrite: bytes => invoke('backup:auto-write', bytes),
  autoRead: entry => invoke('backup:auto-read', entry.name, entry.location),
  autoSetEnabled: enabled => invoke('backup:auto-set-enabled', enabled),
  chooseFolder: () => invoke('backup:choose-folder'),
  reveal: () => invoke('backup:reveal'),
  passwordStatus: () => invoke('backup:password-status'),
  setPassword: password => invoke('backup:set-password', password),
  clearPassword: () => invoke('backup:clear-password'),
  encrypt: plain => invoke('backup:encrypt', plain),
  decrypt: (data, params, password) => invoke('backup:decrypt', data, params, password),
}

const BackupBridgeContext = createContext<BackupBridge>(electronBackupBridge)

export function BackupBridgeProvider({ bridge, children }: { bridge: BackupBridge; children: ReactNode }) {
  return <BackupBridgeContext.Provider value={bridge}>{children}</BackupBridgeContext.Provider>
}

export function useBackupBridge(): BackupBridge {
  return useContext(BackupBridgeContext)
}
